import { Attachment } from './attachment.models';
import type { ApiToken } from './api-token.models';
import { User } from './auth.models';
import { Address, RelatedObject } from './core.models';
import { CustomerSupplier } from './customer-supplier.model';
import { Establishment } from './establishment.model';
import { Role, Roles } from './roles.models';
import { SalesPoint } from './sales-point.models';
import { FreeRequestParams } from './shared.models';
import { Migration } from './invoice.models';

export type Organization = {
  id: string;
  nif: string;
  idu?: string;
  ntd: string;
  taxSystem?: string;
  taxCenter?: string;
  taxDirection?: string;
  accountantRoleId?: string;
  manager?: User;
  name: string;
  activity?: string;
  rccNumber?: string;
  enabled?: boolean;
  phoneNumber?: string;
  email?: string;
  alertLimitSticker?: number;
  footerPage?: string;
  stampIncluded?: boolean;
  stampsReceiptLabel?: string;
  defaultEstablishment?: Establishment;
  bankAccount?: BankAccount;
  billingEquipments?: BillingEquipment[];
  cashRegistersNumber?: number;
  rccmNumber?: string;
  onboardingCompleted?: boolean;
  statusLabel: OrganizationStatusLabel;
  logo?: Attachment | undefined;
  activitySector?: string;
  customerSupplier: CustomerSupplier;
  enabledApiToken?: boolean;
  metadata?: Migration | any[];
  apiKey?: ApiToken;
  updatedAt?: string;
  createdAt?: string;
  onboardingCompletedAt?: string;
};

export enum OrganizationStatusLabel {
  ACTIVE = 'active',
  ADMINISTRATIVE_CESSATION = 'Cessation administrative',
  CESSATION = 'cessation',
  IN_ACTIVITY = 'En activité'
}

export type OrganisationFormValues = Pick<
  Organization,
  'rccNumber' | 'email' | 'phoneNumber' | 'activity'
> &
  Address & {
    siteOwnerName: string;
    siteOwnerNif: string;
    siteOwnerPhoneNumber: string;
    siteOwnerEmail: string;
  };

export enum ReferenceDataType {
  electronicInvoicingApplication = 'electronic_invoicing_application',
  mobileApp = 'mobile_app',
  taxReceiptIssuingTerminal = 'tax_receipt_issuing_terminal',
  mobileTaxReceiptApplication = 'mobile_tax_receipt_application',
  api = 'api',
  refDistrict = 'ref_district',
  refCity = 'ref_city',
  refCountry = 'ref_country',
  salesPointEquipment = 'sales_point_equipment',
  productCategory = 'product_category'
}

export type BillingEquipment = {
  id: string;
  type: string;
  name: string;
  label?: string;
  otherValue?: string;
  value?: string;
  createdAt: string;
  updatedAt: string;
};

export type OrganizationCreationParams = Pick<
  Organization,
  | 'name'
  | 'activity'
  | 'rccNumber'
  | 'email'
  | 'phoneNumber'
  | 'cashRegistersNumber'
  | 'billingEquipments'
  | 'alertLimitSticker'
  | 'enabled'
> & { address: Partial<Address> };

export type OrganizationUpdateParams = Partial<
  Pick<Organization, 'name' | 'activity'>
>;

export type OrganizationHeadquartersCreationParams = Pick<
  Establishment,
  'name' | 'siteOwner' | 'ownerPhoneNumber' | 'email'
> & { address: Partial<Address> };

export type OrganizationCreationBuildPayloadsParams = {
  billingEquipment: {
    equipments: {
      type: ReferenceDataType;
      name: string;
      otherValue: string;
    }[];
    cashRegistersNumber: number | string;
  };
  location: Address & {
    rccNumber: string;
    email: string;
    phoneNumber: string;
  } & {
    siteOwnerName: string;
    siteOwnerNif: string;
    siteOwnerPhoneNumber: string;
    siteOwnerEmail: string;
  };
  currentUser: User;
};

export type OrganizationListParams = FreeRequestParams & {
  name?: string;
  ntd?: string;
  nif?: string;
  enabled?: string;
  taxSystem?: string;
  phoneNumber?: string;
  email?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type BankAccount = {
  accountHolderName: string;
  bankCode: string;
  bankName: string;
  accountNumber: string;
  ribKey: string;
  iban: string;
  bic: string;
};

export type UploadOrganizationLogoParams = {
  organizationId: string;
  file: Blob | File;
};

export type SiteOwner = {
  name: string;
  nif: string;
  phoneNumber: string;
  email: string;
};

export type Coordinates = { latitude: number; longitude: number };

export type OrganizationUserListParams = FreeRequestParams & {
  role: Role;
  organisation: Organization;
  scope: RelatedObject;
  createdAt: string;
};

export type OrganizationUser = {
  id: string;
  role: Roles;
  organisation?: Organization;
  createdAt: string;
  establishment?: Establishment;
  salesPoints?: SalesPoint[] | null;
};

export type OrganizationUserCreationParams = {
  relatedObject: RelatedObject;
  roleId: string;
};

export type OrganizationUserInfo = Pick<
  User,
  'firstName' | 'lastName' | 'role'
>;
export type OrganizationUserProfilForms = Partial<{
  roleId: string | null;
  establishment: Establishment[] | Establishment | null;
  salesPoint: SalesPoint[] | SalesPoint | null;
}>;

export type UserActionHandler = (element: OrganizationUser) => void;

export type OrganizationUserScopeCreationParams = {
  establishmentId: string | undefined;
  roleId: string | undefined;
  salesPoints: Array<{ id: string }> | string[];
};

export type OrganizationFiltersValues = {
  taxSystems: string[];
  activitySectors: string[];
  taxDirections: string[];
  accountRoleLabels: string[];
};
